import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    IconButton,
    InputAdornment,
    Alert, // Import Alert for error messages
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import config from './config.js';
import api from './api'; // Assuming you might want to use your api helper for consistency

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(''); // State for login errors
    const navigate = useNavigate();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(''); // Clear previous errors

        try {
            // Using the api helper might be cleaner if it's set up for unauthenticated requests too
            // const response = await api.post(`${config.backendUrl}/login`, { email, password });
            const response = await fetch(`${config.backendUrl}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            console.log('Login Response Status:', response.status);
            const data = await response.json();
            console.log('Login Response Data:', data);

            if (response.ok) {
                if (data.token && data.token.split('.').length === 3) {
                    console.log("Login successful, setting localStorage...");
                    localStorage.setItem('token', data.token);
                    localStorage.setItem('userId', data.user_id); // Provided directly by backend
                    localStorage.setItem('userRole', data.role_id); // Provided directly by backend
                    
                    // Store userName from direct response data
                    if (data.name) {
                        localStorage.setItem('userName', data.name);
                        console.log("userName set in localStorage from direct data:", data.name);
                    } else {
                        // Fallback if name is not directly in response (though it should be now)
                        const decodedToken = JSON.parse(atob(data.token.split('.')[1]));
                        localStorage.setItem('userName', decodedToken.name || ''); // Use empty string if not in token
                        console.log("userName set in localStorage from token:", decodedToken.name);
                    }

                    // Store userCohortId from direct response data
                    if (data.cohort_id !== null && data.cohort_id !== undefined) {
                        localStorage.setItem('userCohortId', data.cohort_id.toString());
                        console.log("userCohortId set in localStorage:", data.cohort_id);
                    } else {
                        localStorage.removeItem('userCohortId'); // Clear if not provided or null
                        console.log("userCohortId removed from localStorage as it was not provided.");
                    }
                    
                    console.log("Dispatching authChanged event after setting all localStorage items...");
                    window.dispatchEvent(new Event('authChanged'));
                    
                    console.log("Navigating to /dashboard...");
                    navigate('/dashboard');

                } else {
                    console.error('Login Error: Invalid or missing token in response.', data);
                    setError('Login failed: Invalid response from server.');
                }
            } else {
                const errorMessage = data.message || `Login failed (Status: ${response.status})`;
                console.error('Login failed:', errorMessage);
                setError(errorMessage);
            }
        } catch (error) {
            console.error('An error occurred during login fetch:', error);
            setError('Login failed: Could not connect to server or unexpected error.');
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            sx={{
                marginTop: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                '& .MuiTextField-root': { m: 1, width: '30ch' },
            }}
        >
            <Typography variant="h5" component="h1" gutterBottom>
                Login
            </Typography>
            {error && (
                <Alert severity="error" sx={{ width: '100%', maxWidth: '30ch', mb: 2 }}>
                    {error}
                </Alert>
            )}
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2} direction="column" alignItems="center">
                     <Grid item sx={{ width: '100%', maxWidth: '35ch' }}>
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item sx={{ width: '100%', maxWidth: '35ch' }}>
                        <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ width: '100%', maxWidth: '35ch' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                        >
                            Login
                        </Button>
                    </Grid>
                </Grid>
            </form>
             <Box mt={2}>
                 <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                     <Typography variant="body2" color="primary">
                        Forgot Password?
                     </Typography>
                 </Link>
             </Box>
             <Box mt={1}>
                 <Link to="/register" style={{ textDecoration: 'none' }}>
                     <Typography variant="body2" color="primary">
                        Don't have an account? Register here.
                     </Typography>
                 </Link>
             </Box>
        </Box>
    );
}

export default Login;
